"use strict";
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState
} from "react";
import { assignInlineVars } from "@vanilla-extract/dynamic";
import classNames from "classnames";
import { randomString } from "../../../utils";
import * as styles from "./SlideTransition-css";
const SlideTransitionContext = createContext({
  id: "",
  panels: [],
  currentPanel: "",
  registerPanel: () => null,
  unregisterPanel: () => null,
  onTransitionStart: () => null,
  animating: false
});
export function SlideTransition({ children, value, className }) {
  const [currentPanel, setCurrentPanel] = useState(value);
  const [panels, setPanels] = useState([]);
  const [panelHeight, setPanelHeight] = useState("auto");
  const [panelWidth, setPanelWidth] = useState("auto");
  const [animating, setAnimating] = useState(false);
  const panelsRef = useRef([]);
  const transitionContainerRef = useRef(null);
  const transitionId = useRef(`slide-transition-${randomString()}`);
  const { animating: parentAnimating } = useContext(SlideTransitionContext);
  useEffect(() => {
    const prevPanel = currentPanel;
    const nextPanel = value;
    const prevPanelIndex = panelsRef.current.indexOf(prevPanel);
    const nextPanelIndex = panelsRef.current.indexOf(nextPanel);
    if (prevPanel === nextPanel) return;
    if (!document.startViewTransition) {
      return setCurrentPanel(nextPanel);
    }
    setAnimating(true);
    if (transitionContainerRef.current) {
      setPanelHeight(`${transitionContainerRef.current.offsetHeight}px`);
      setPanelWidth(`${transitionContainerRef.current.offsetWidth}px`);
    }
    const transition = document.startViewTransition(() => {
      setCurrentPanel(nextPanel);
    });
    if (nextPanelIndex > prevPanelIndex) {
      transition.types.add(`${transitionId.current}-forward`);
    } else {
      transition.types.add(`${transitionId.current}-backward`);
    }
  }, [value]);
  const updatePanels = useCallback((panels2) => {
    setPanels(panels2);
    panelsRef.current = panels2;
  }, []);
  const onTransitionStart = useCallback(
    (panelRef) => {
      const prevPanel = currentPanel;
      const nextPanel = value;
      if (prevPanel === nextPanel) return;
      requestAnimationFrame(() => {
        if (panelRef.current) {
          setPanelHeight(`${panelRef.current.offsetHeight}px`);
          setPanelWidth(`${panelRef.current.offsetWidth}px`);
        }
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [value]
  );
  const registerPanel = useCallback(
    (panel) => {
      const newPanels = [...panelsRef.current, panel];
      updatePanels(newPanels);
    },
    [updatePanels]
  );
  const unregisterPanel = useCallback(
    (panel) => {
      const newPanels = panelsRef.current.filter((item) => item !== panel);
      updatePanels(newPanels);
    },
    [updatePanels]
  );
  return /* @__PURE__ */ React.createElement(
    SlideTransitionContext.Provider,
    {
      value: {
        panels,
        currentPanel,
        onTransitionStart,
        registerPanel,
        unregisterPanel,
        id: transitionId.current,
        animating
      }
    },
    /* @__PURE__ */ React.createElement("style", null, generateStyles(transitionId.current, parentAnimating)),
    /* @__PURE__ */ React.createElement(
      "div",
      {
        ref: transitionContainerRef,
        className: classNames(styles.container({ animating }), className),
        style: assignInlineVars({ [styles.panelHeight]: panelHeight, [styles.panelWidth]: panelWidth }),
        onTransitionEnd: (e) => {
          if (e.target !== transitionContainerRef.current) return;
          setAnimating(false);
          setPanelHeight("auto");
          setPanelWidth("auto");
        }
      },
      children
    )
  );
}
export function SlideTransitionPanel({
  children,
  value,
  className,
  style
}) {
  const { currentPanel, registerPanel, unregisterPanel, onTransitionStart, id } = useContext(SlideTransitionContext);
  const visible = value === currentPanel;
  const panelRef = useRef(null);
  useEffect(() => {
    if (visible) {
      onTransitionStart(panelRef);
    }
  }, [visible, onTransitionStart]);
  useEffect(() => {
    registerPanel(value);
    return () => unregisterPanel(value);
  }, [registerPanel, unregisterPanel, value]);
  if (!visible) return null;
  return /* @__PURE__ */ React.createElement("div", { className: classNames(styles.panel, className, id), style }, /* @__PURE__ */ React.createElement("div", { ref: panelRef }, children));
}
const generateStyles = (id, parentAnimating) => {
  const animationStyles = `
  :active-view-transition-type(${id}-forward, ${id}-backward) {
    &::view-transition-group(root) {
      animation: none;
    }
  }

  :active-view-transition-type(${id}-forward) {
    &::view-transition-old(${id}) {
      animation-name: ${styles.slideOutLeft};
    }

    &::view-transition-new(${id}) {
      animation-name: ${styles.slideInRight};
    }
  }

  :active-view-transition-type(${id}-backward) {
    &::view-transition-old(${id}) {
      animation-name: ${styles.slideOutRight};
    }

    &::view-transition-new(${id}) {
      animation-name: ${styles.slideInLeft};
    }
  }`;
  if (parentAnimating) {
    return animationStyles;
  }
  return `
  ${animationStyles}
  
  .${id} {
    view-transition-name: ${id};
  }`;
};
